import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Trash2, Plus, CheckCircle2, AlertCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import * as takata from '../services/takataService';
import { RaccoltaCounts, TelaioRaccolto } from '../services/takataService';
import { MARCHE_TAKATA, MarcaTakata } from '../data/takataMarche';

function normalizeVin(raw: string) {
  return (raw || '').toUpperCase().replace(/[\s\-_.]/g, '');
}

const BrandLogo: React.FC<{ m: MarcaTakata }> = ({ m }) => {
  const [err, setErr] = useState(false);
  if (m.slug && !err) {
    return (
      <img
        src={`/images/takata-marchi/${m.slug}.jpg`}
        alt={m.nome}
        onError={() => setErr(true)}
        className="w-10 h-10 object-contain flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300 flex-shrink-0">
      {m.nome.slice(0, 3).toUpperCase()}
    </div>
  );
};

const TakataRaccoltaPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [counts, setCounts] = useState<RaccoltaCounts>({});
  const [mie, setMie] = useState<TelaioRaccolto[]>([]);
  const [loading, setLoading] = useState(true);

  const [marca, setMarca] = useState('');
  const [vin, setVin] = useState('');
  const [esito, setEsito] = useState<'non_coinvolto' | 'coinvolto'>('non_coinvolto');
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await takata.getRaccolta();
      setCounts(r.counts || {});
      setMie(r.mie || []);
    } catch (e) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const vinNorm = normalizeVin(vin);
  const vinValid = vinNorm.length === 17 && !/[IOQ]/.test(vinNorm);

  const submit = async () => {
    if (!marca) { toast.error('Scegli la marca'); return; }
    if (!vinValid) { toast.error('Telaio non valido (17 caratteri)'); return; }
    setSaving(true);
    try {
      await takata.addRaccolta({ marca, vin: vinNorm, esito, nota });
      toast.success('Telaio inserito, grazie!');
      setVin(''); setNota('');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const rimuovi = async (id: string) => {
    try {
      await takata.deleteRaccolta(id);
      await load();
    } catch (e) {
      toast.error('Errore nella rimozione');
    }
  };

  const coperti = MARCHE_TAKATA.filter((m) => {
    const c = counts[m.nome];
    return c && (c.coinvolto > 0 || c.non_coinvolto > 0);
  }).length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/area-riservata')} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Torna all'area riservata
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Raccolta telai per il richiamo Takata</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Aiutaci a raccogliere telai (coinvolti e non) per ogni marca</p>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-5 text-sm text-blue-800 dark:text-blue-200">
          Stiamo preparando il sistema di verifica automatica. Per tararlo su tutti i marchi ci servono
          <strong> telai reali</strong>: se hai espletato pratiche di veicoli interessati dalle marche qui sotto,
          inserisci il numero di telaio indicando se è risultato <strong>coinvolto</strong> o <strong>non coinvolto</strong>.
          I casi <strong>coinvolti</strong> sono i più preziosi e rari. Grazie!
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Marca</label>
              <select value={marca} onChange={(e) => setMarca(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
                <option value="">Seleziona…</option>
                {MARCHE_TAKATA.map((m) => <option key={m.nome} value={m.nome}>{m.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Telaio (VIN)</label>
              <input value={vin} onChange={(e) => setVin(e.target.value)} placeholder="17 caratteri" maxLength={25}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm uppercase" />
              <span className={`text-2xs ${vinNorm.length === 17 ? 'text-green-600' : 'text-gray-400'}`}>{vinNorm.length}/17</span>
            </div>
          </div>

          <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mt-3 block">Esito</label>
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={() => setEsito('non_coinvolto')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${esito === 'non_coinvolto' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'}`}>
              Non coinvolto
            </button>
            <button type="button" onClick={() => setEsito('coinvolto')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${esito === 'coinvolto' ? 'bg-red-600 text-white border-red-600' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'}`}>
              Coinvolto
            </button>
          </div>

          <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota (opzionale)"
            className="mt-3 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />

          <button onClick={submit} disabled={saving || !marca || !vinValid}
            className="mt-4 w-full py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-600 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Inserisci telaio
          </button>
        </div>

        {/* Bacheca copertura */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">Copertura marchi</h2>
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Coperti {coperti}/{MARCHE_TAKATA.length}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mb-6">
          {MARCHE_TAKATA.map((m) => {
            const c = counts[m.nome] || { coinvolto: 0, non_coinvolto: 0 };
            const vuoto = c.coinvolto === 0 && c.non_coinvolto === 0;
            return (
              <div key={m.nome} className={`flex items-center gap-3 rounded-lg border p-2.5 ${vuoto ? 'border-orange-300 dark:border-orange-700 bg-orange-50/50 dark:bg-orange-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
                <BrandLogo m={m} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{m.nome}</p>
                  {vuoto && <p className="text-2xs text-orange-600 dark:text-orange-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> nessun telaio</p>}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <span title="Non coinvolti" className={`text-xs px-2 py-1 rounded-md font-semibold ${c.non_coinvolto > 0 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-gray-100 text-gray-400 dark:bg-gray-700'}`}>{c.non_coinvolto}</span>
                  <span title="Coinvolti" className={`text-xs px-2 py-1 rounded-md font-semibold ${c.coinvolto > 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-400 dark:bg-gray-700'}`}>{c.coinvolto}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Le mie segnalazioni */}
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Le mie segnalazioni ({mie.length})</h2>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento…</p>
        ) : mie.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Non hai ancora inserito telai.</p>
        ) : (
          <div className="space-y-1.5">
            {mie.map((t) => (
              <div key={t._id} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
                {t.esito === 'coinvolto'
                  ? <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  : <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-sm text-gray-900 dark:text-white">{t.vin}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{t.marca}</span>
                </div>
                <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${t.esito === 'coinvolto' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}>
                  {t.esito === 'coinvolto' ? 'Coinvolto' : 'Non coinvolto'}
                </span>
                <button onClick={() => rimuovi(t._id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TakataRaccoltaPage;
